import { Build, Component, Element, Host, Prop, State, VNode, h } from '@stencil/core';
import { Width } from '../types';
import { BadgeSize, BadgePalette } from './types';

/** @slot - Adds badge text. */
@Component({
  shadow: true,
  styleUrl: 'badge.css',
  tag: 'abds-badge',
})
export class Badge {
  //--------------------------------------------------------------------------
  //
  //  Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsBadgeElement;

  //--------------------------------------------------------------------------
  //
  //  Props
  //
  //--------------------------------------------------------------------------

  /** Adds an abds-icon to end of badge text. */
  @Prop({ reflect: true }) iconEnd: string;

  /** Adds an abds-icon to beginning of badge text. */
  @Prop({ reflect: true }) iconStart: string;

  /** Sets badge color options. */
  @Prop({ reflect: true }) palette: BadgePalette = 'basic';

  /** Sets height and padding of badge. */
  @Prop({ reflect: true }) size: BadgeSize = 'base';

  /** Sets badge width to auto fit content or full. */
  @Prop({ reflect: true }) width: Width = 'auto';

  //--------------------------------------------------------------------------
  //
  //  Private Properties
  //
  //--------------------------------------------------------------------------

  /** watches for changing text content **/
  private textObserver: MutationObserver;

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private setHasContent = () => {
    this.hasContent = this.host.textContent.trim().length > 0 || this.host.childNodes.length > 0;
  };

  //--------------------------------------------------------------------------
  //
  //  Private State
  //
  //--------------------------------------------------------------------------

  /** determine if slotted content present for styling purposes */
  @State() private hasContent = false;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  componentWillLoad(): void {
    if (Build.isBrowser) {
      this.setHasContent();
    }
  }

  connectedCallback(): void {
    if (Build.isBrowser) {
      this.textObserver = new MutationObserver(() => {
        this.setHasContent();
      });

      this.textObserver.observe(this.host, { childList: true, subtree: true });
    }
  }

  disconnectedCallback(): void {
    this.textObserver.disconnect();
  }

  render(): VNode {
    const { hasContent, iconEnd, iconStart } = this;

    return (
      <Host>
        {iconStart && <abds-icon icon={iconStart} size="sm" stroke-width="semibold" />}
        {hasContent && (
          <div class="text-wrapper">
            <slot />
          </div>
        )}
        {iconEnd && <abds-icon icon={iconEnd} size="sm" stroke-width="semibold" />}
      </Host>
    );
  }
}
